import { ToastPosition, toast } from "react-hot-toast";
import { Snackbar } from "@/components/atoms";

export interface SnackbarConfig {
  title?: string;
  message: string;
  actionText?: string;
  onActionClick?: () => void;
  timestamp?: string;
  icon?: React.ReactNode;
  size?: "default" | "small";
  textMode?: "single" | "multiple";
  duration?: number;
  /**
   * Reuse one toast instead of stacking a new one.
   *
   * react-hot-toast updates the existing toast in place when handed the same id, which is what lets
   * a long-running job report progress — "40 of 100 processed" becoming "60 of 100" — without a
   * column of stale snackbars behind it. Omit for one-off messages.
   */
  id?: string;
  position?: ToastPosition;
}

export const showSnackbar = (config: SnackbarConfig) => {
  const {
    title,
    message,
    actionText,
    onActionClick,
    timestamp,
    icon,
    size = "default",
    textMode = "single",
    duration = 8000, // Longer duration for snackbars since they typically contain more info
    id,
    position,
  } = config;

  const { innerWidth: width } = window;

  const handleActionClick = () => {
    if (onActionClick) {
      onActionClick();
    }
    // Auto-dismiss the snackbar when action is clicked
    toast.dismiss();
  };

  toast.custom(
    (t) => (
      <Snackbar
        size={size}
        textMode={textMode}
        title={title}
        message={message}
        actionText={actionText}
        onActionClick={actionText ? handleActionClick : undefined}
        timestamp={timestamp}
        icon={icon}
        visible={t.visible}
        onClose={() => toast.dismiss(t.id)}
      />
    ),
    {
      position: position ?? (width > 768 ? "bottom-right" : "bottom-center"),
      duration,
      ...(id ? { id } : {}),
    }
  );
};
